import { PropsWithChildren, useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  ArrayFieldState,
  useArrayFieldItemApi,
  useArrayFieldItemState,
} from 'informed';
import { useFormWideErrors } from '../../../../QuoteWizard/QuoteForms/FormWideErrorContext';
import { safelyGetValues } from '../../../../../util';

interface VagoLocationAccordionWrapperProps extends PropsWithChildren<{}> {
  justAdded: boolean;
  setJustAdded: (b: boolean) => void;
  openIndex: number | null;
  setOpenIndex: (i: number | null) => void;
  fields: ArrayFieldState['fields'];
}

/**
 * Wrapper component that conditionally wraps location fields in an Accordion
 * for Vago organization. All logic for showing/hiding the accordion is here.
 */
export const VagoLocationAccordionWrapper = ({
  children,
  justAdded,
  setJustAdded,
  openIndex,
  setOpenIndex,
  fields,
}: VagoLocationAccordionWrapperProps) => {
  const [addressDisplay, setAddressDisplay] = useState<string>('Location');
  const [hasErrors, setHasErrors] = useState<boolean>(false);
  const [okToUpdateFormErrors, setOkToUpdateFormErrors] =
    useState<boolean>(true);
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const okToValidate = useRef<boolean>(false);

  const { errors, values, index, key } = useArrayFieldItemState();
  const arrayFieldItemApi = useArrayFieldItemApi();
  const { error: formWideError, setError: setFormWideError } =
    useFormWideErrors(key);

  // if it's the initial mount and there's only one field, keep it open
  useEffect(() => {
    if (fields.length === 1) {
      setOpenIndex(0);
    }
    setTimeout(() => {
      okToValidate.current = true;
    }, 100);
  }, []);

  // Update isOpen based on openIndex
  // Note: Validation happens here when opening or closing (when openIndex changes)
  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined = undefined;

    if (openIndex === index) {
      setIsOpen(true);
    } else if (!justAdded && isOpen && openIndex !== index) {
      // Currently open but index doesn't match - validate and close (as long as we haven't just added it)
      if (okToValidate.current) {
        // @ts-expect-error
        arrayFieldItemApi.validate();
      }
      timeout = setTimeout(() => {
        setIsOpen(false);
      }, 100);
    }

    // Cleanup: clear timeout on unmount or when dependencies change
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [openIndex, index, justAdded]);

  // Set openIndex when justAdded is true - however, we first want to wait for it to be open
  useEffect(() => {
    if (isOpen && justAdded) {
      setOpenIndex(index);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [justAdded, index, isOpen]);

  useEffect(() => {
    if (isOpen && okToValidate.current) {
      // Run validation when accordion is opened to surface missing fields
      // @ts-expect-error informed types are loose here
      arrayFieldItemApi.validate();
      setOkToUpdateFormErrors(true);
    }
  }, [isOpen, arrayFieldItemApi]);

  // set formwide error as a result of the individual errors
  useEffect(() => {
    const shouldUpdate = isOpen || okToUpdateFormErrors;
    if (shouldUpdate) {
      if (errors && Object.keys(errors).length > 0) {
        setFormWideError(`Error in ${addressDisplay}`);
      } else {
        setFormWideError(undefined);
      }
      if (!isOpen) {
        setOkToUpdateFormErrors(false);
      }
    }
  }, [isOpen, okToUpdateFormErrors, errors ? Object.keys(errors).length : 0]);

  // are we in an error state?
  useEffect(() => {
    const hasIndividualErrors = (errors ? Object.keys(errors).length : 0) > 0;
    const hasFormWideError = !!formWideError;
    setHasErrors(hasIndividualErrors || hasFormWideError);
  }, [errors ? Object.keys(errors).length : 0, formWideError]);

  // set the display address
  useEffect(() => {
    const { address, city, province, postal } = safelyGetValues(values || {}, [
      'address',
      'city',
      'province',
      'postal',
    ]);
    const addressComponents = [address, city, province, postal].filter(
      (e) => !!e,
    );
    const toDisplay =
      addressComponents.length > 0
        ? addressComponents.join(', ')
        : `Location #${index + 1}`;
    setAddressDisplay(toDisplay);
  }, [
    values?.['address'],
    values?.['city'],
    values?.['province'],
    values?.['postal'],
    index,
  ]);

  // Handler for collapse button - validates then collapses
  const handleCollapse = () => {
    if (okToValidate.current) {
      // @ts-expect-error
      arrayFieldItemApi.validate();
    }
    setOpenIndex(null);
    if (justAdded) {
      setJustAdded(false);
    }
  };

  // Cleanup: clear formwide error when component unmounts (e.g., when location is removed)
  useEffect(() => {
    return () => {
      // Clear error when component is unmounted
      setFormWideError(undefined);
    };
  }, [setFormWideError]);

  return (
    <StyledAccordion
      defaultExpanded={true}
      disableGutters
      elevation={0}
      expanded={isOpen}
      TransitionProps={{ unmountOnExit: true }}
      onChange={(_event, isExpanded) => {
        if (isExpanded) {
          // just expanded - set this location's index as the open index
          setOpenIndex(index);
          setFormWideError(undefined);
          setOkToUpdateFormErrors(true);
          if (okToValidate.current) {
            setTimeout(() => {
              // @ts-expect-error informed types are loose here
              arrayFieldItemApi.validate();
            }, 0);
          }
        } else {
          // just collapsed - set open index to null
          // Note: Validation happens in the useEffect when openIndex changes
          // Don't collapse if this location was just added (prevent bug on initial mount)
          if (!justAdded) {
            setOpenIndex(null);
            setJustAdded(false);
          }
        }
      }}
    >
      <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
        <StyledHeaderTypography variant='subtitle1' hasErrors={hasErrors}>
          <span>
            {addressDisplay}
            {hasErrors ? (
              <ErrorOutlineIcon color='error' sx={{ ml: 1, display: 'flex' }} />
            ) : null}
          </span>
        </StyledHeaderTypography>
      </StyledAccordionSummary>
      <StyledAccordionDetails>
        {children}
        <ButtonContainer>
          <Button
            onClick={handleCollapse}
            variant='outlined'
            size='small'
            startIcon={<KeyboardArrowUpIcon />}
          >
            Collapse Location
          </Button>
        </ButtonContainer>
      </StyledAccordionDetails>
    </StyledAccordion>
  );
};

// Styled Accordion with transparent background and no borders/shadows/margins
const StyledAccordion = styled(Accordion)({
  boxShadow: 'none',
  backgroundColor: 'transparent',
  backgroundImage: 'none',
  border: 'none',
  borderRadius: 0,
  margin: 0,
  '&:before': {
    display: 'none',
  },
  '&:after': {
    display: 'none',
  },
  '&.Mui-expanded': {
    margin: 0,
    boxShadow: 'none',
  },
  '&.MuiAccordion-root': {
    boxShadow: 'none',
    '&:before': {
      display: 'none',
    },
  },
  '& .MuiAccordion-region': {
    backgroundColor: 'transparent',
  },
});

// Styled AccordionSummary with no padding and transparent background
const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: 0,
  minHeight: 'unset',
  backgroundColor: 'transparent',
  boxShadow: 'none',
  alignItems: 'center',
  '&.MuiAccordionSummary-root': {
    alignItems: 'center',
  },
  '&:hover': {
    backgroundColor: 'transparent',
  },
  '&.Mui-expanded': {
    minHeight: 'unset',
    backgroundColor: 'transparent',
    margin: 0,
  },
  '& .MuiAccordionSummary-content': {
    margin: theme.spacing(1.5, 0),
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiAccordionSummary-content.Mui-expanded': {
    margin: theme.spacing(1.5, 0),
    display: 'flex',
    alignItems: 'center',
  },
  '& .MuiAccordionSummary-expandIconWrapper': {
    color: theme.palette.primary.main,
    marginLeft: theme.spacing(1),
    marginTop: 0,
    marginBottom: 0,
    alignSelf: 'center',
  },
}));

// Styled Typography for the accordion header
const StyledHeaderTypography = styled(Typography)<{ hasErrors: boolean }>(
  ({ theme, hasErrors }) => ({
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
    color: hasErrors ? theme.palette.error.main : theme.palette.primary.main,
    lineHeight: 1,
    '& > span': {
      display: 'flex',
      alignItems: 'center',
    },
  }),
);

// Styled AccordionDetails with no padding/margin
const StyledAccordionDetails = styled(AccordionDetails)({
  padding: 0,
  margin: 0,
});

// Styled container for centering the collapse button
const ButtonContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 'auto',
  paddingTop: theme.spacing(2),
}));
